import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  MapPin,
  Globe,
  Search,
  Share2,
  Code,
  Wrench,
  HelpCircle,
  CheckCircle,
  ChevronDown,
  Layers,
} from "lucide-react";

const services = [
  {
    icon: Globe,
    title: "Website Development",
    description: "Custom websites built for speed, clarity, and conversion.",
    link: "/services/website-development",
  },
  {
    icon: Search,
    title: "SEO Services",
    description:
      "Search engine optimisation that helps the right clients in Richmond find you.",
    link: "/services/seo-services",
  },
  {
    icon: Share2,
    title: "Social Media Marketing",
    description:
      "Consistent content and strategy that help your brand show up and build trust.",
    link: "/services/social-media-marketing",
  },
  {
    icon: Code,
    title: "Custom Tool Creation",
    description:
      "Dashboards, calculators, and custom web applications built around your workflow.",
    link: "/services/custom-tool-creation",
  },
  {
    icon: Wrench,
    title: "Maintenance & Support",
    description:
      "Ongoing updates, fixes, and technical support to keep everything performing well.",
    link: "/services/maintenance-support",
  },
  {
    icon: HelpCircle,
    title: "Not Sure What You Need?",
    description:
      "Tell us the problem. We will help you find the right digital solution.",
    link: "/contact",
  },
];

const whyPoints = [
  "Industry research before we build anything",
  "Strategy before execution — always",
  "Custom solutions — not templates",
  "Ongoing support after launch",
];

const projects = [
  {
    title: "Punjab Financial & Insurance Services",
    description:
      "An insurance brand serving the Lower Mainland community had no consistent digital presence. We built the full content strategy — brand voice, content pillars, and posting schedule across four platforms.",
    link: "/services/social-media-marketing",
  },
  {
    title: "Canada RDSP + Punjab RDSP",
    description:
      "Insurance agents had no tool to calculate RDSP grants and bonds for their clients. We built two custom branded calculators — canadardsp.com and punjabrdsp.com — now used by agents serving families across BC and Canada.",
    link: "/services/custom-tool-creation",
  },
  {
    title: "Big Boys Motor Club",
    description:
      "A car brand was spending hours updating listings manually. We built a custom admin dashboard so they could manage inventory and post listings themselves — no developer needed.",
    link: "/services/custom-tool-creation",
  },
];

const faqs = [
  {
    question: "Do you work with businesses in Richmond?",
    answer:
      "Yes. We work with businesses across Richmond and the Lower Mainland. Most of our work is done remotely so location is not a barrier.",
  },
  {
    question: "What services do you offer Richmond businesses?",
    answer:
      "Website development, SEO services, social media marketing, custom tool creation, and maintenance and support. Most clients use a combination depending on what their business actually needs.",
  },
  {
    question:
      "How is Square Trigger different from other digital agencies near Richmond?",
    answer:
      "We do not start with a package. We start by understanding the problem your business needs to solve online, then build the right solution around that. Every project is custom — not a template with your logo added.",
  },
  {
    question: "How do I get started?",
    answer:
      "Book a free consultation through our contact page. We will talk about your business, what is not working online, and what the right next step looks like.",
  },
];
const HERO_IMAGE = "/richmond.webp";

export default function RichmondDigitalAgencyPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
     <SEO
  title="Digital Marketing agency richmond BC | Square Trigger"
  description="Square Trigger is a Digital Marketing Agency in Richmond, BC. We build Websites, SEO strategies, Social media advertising, and Custom tools for businesses."
  ogTitle="Digital Marketing agency richmond BC | Square Trigger"
  ogDescription="Square Trigger is a Digital Marketing Agency in Richmond, BC. We build Websites, SEO strategies, Social media advertising, and Custom tools for businesses."
  ogSiteName="Square Trigger Richmond BC"
  ogImage="https://squaretrigger.com/logo3.webp"
/>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
  <div className="absolute inset-0">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${HERO_IMAGE})` }}
    />
  </div>

        <div className="container-custom relative z-10 px-5 pt-[20px] pb-[44px] sm:px-5 sm:pt-[90px] sm:pb-[56px] lg:pt-[60px] lg:pb-[88px]">
          <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-5 flex justify-center sm:mb-2 lg:justify-start"
              >
                <Badge className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/20 px-3 py-2 text-primary-foreground sm:px-4">
                  <MapPin className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Digital Agency in Richmond, BC
                  </span>
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className='mx-auto m-0 mb-[18px] max-w-[900px] font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,3rem)] font-extrabold leading-[1.15] text-background lg:mx-0'
              >
                Digital Agency in Richmond That Builds What Your Business{" "}
                <span className="gradient-text">Actually Needs Online</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mx-auto m-0 mb-6 max-w-[720px] text-[1.08rem] font-medium leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] lg:mx-0"
              >
                Websites. SEO. Social media. Custom tools. Built around your
                business goals — not a generic package. Serving Richmond and the
                surrounding Lower Mainland.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col flex-wrap items-stretch justify-center gap-[14px] sm:flex-row sm:items-center lg:justify-start"
              >
                <Button
                  variant="gradient"
                  asChild
                  className="group inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] px-7 py-[14px] text-[1rem] font-bold sm:w-auto"
                >
                  <Link to="/contact" className="flex items-center gap-2">
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <Button
                  variant="glass"
                  asChild
                  className="inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] border border-white/20 bg-white/10 px-7 py-[14px] text-[1rem] font-bold text-white transition-all duration-300 hover:-translate-y-[2px] hover:bg-white/10 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
                >
                  <Link to="/work">See Our Work</Link>
                </Button>
                            </motion.div>
            </div>

            <div className="hidden lg:flex items-center justify-center min-h-[420px]">
              <div className="h-[420px] w-full max-w-[520px]" />
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-20 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Serving Richmond
            </span>

            <h2 className="mt-3 mb-5 text-3xl font-display font-bold text-foreground md:text-4xl">
              We Work With Richmond Businesses That Need a{" "}
              <span className="gradient-text">Stronger Online Presence</span>
            </h2>

            <div className="rounded-2xl border border-border bg-card/60 p-6 shadow-soft backdrop-blur sm:p-8">
              <p className="mx-auto max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                Richmond is home to one of the most diverse and active business
                communities in Metro Vancouver. From independent insurance
                brokers and financial advisors to retail businesses, restaurants,
                automotive brands, and professional services — competition for
                online visibility in Richmond is real and growing. Square
                Trigger works with Richmond businesses that need to be found more
                easily, communicate more clearly online, and convert more
                visitors into clients. We build websites, SEO strategies, social
                media systems, and custom digital tools around what the business
                actually needs — not a standard package.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-14 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              What We Do
            </span>

            <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
              Digital Services for{" "}
              <span className="gradient-text">Richmond Businesses</span>
            </h2>

            <p className="mx-auto mt-4 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              We help Richmond businesses improve visibility, strengthen trust,
              and solve digital problems with practical services built around
              real goals.
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;

              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="h-full"
                >
                  <Card className="card-hover flex h-full flex-col items-center border-border p-6 text-center transition-all duration-300 hover:border-primary/30 sm:items-start sm:text-left">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl gradient-primary sm:mx-0">
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>

                    <h3 className="mb-2 text-lg font-display font-semibold text-foreground sm:text-xl">
                      {service.title}
                    </h3>

                    <p className="mb-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
                      {service.description}
                    </p>

                    <Link
                      to={service.link}
                      className="mt-auto inline-flex items-center gap-2 text-primary hover:underline"
                    >
                      Learn More
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Square Trigger */}
      <section className="section-padding relative overflow-hidden bg-foreground text-background">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-10 left-10 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute right-10 bottom-10 h-80 w-80 rounded-full bg-secondary/10 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-secondary">
              Why Choose Us
            </span>

            <h2 className="mt-3 text-3xl font-display font-bold md:text-4xl">
              We Start With Your Problem. Then We Build the{" "}
              <span className="gradient-text">Right Solution.</span>
            </h2>

            <p className="mx-auto mt-4 max-w-[900px] leading-relaxed text-background/70">
              Richmond businesses are competing in one of the most active
              digital markets in the Lower Mainland. We do not come in with a
              standard package. We start by understanding your business, your
              clients, and what is stopping you from growing online — then we
              build the right solution around that.
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {whyPoints.map((point, index) => (
              <motion.div
                key={point}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="rounded-2xl border border-background/10 bg-background/5 p-6 text-center transition-colors hover:border-primary/30 sm:text-left"
              >
                <CheckCircle className="mx-auto mb-4 h-6 w-6 text-primary sm:mx-0" />
                <p className="leading-relaxed text-background/75">{point}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Our Work
            </span>

            <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
              Real Projects.{" "}
              <span className="gradient-text">Real Problems Solved.</span>
            </h2>

            <p className="mx-auto mt-4 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              Every project below started with a specific business problem and a
              solution built around it. We bring the same approach to every
              Richmond business we work with.
            </p>
          </motion.div>

          <div className="grid gap-6 lg:grid-cols-3 lg:gap-8">
            {projects.map((project, index) => (
              <motion.div
                key={project.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="h-full"
              >
                <Card className="card-hover flex h-full flex-col border-border p-6 text-center transition-all duration-300 hover:border-primary/30 sm:text-left">
                  <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-xl gradient-primary-soft sm:mx-0">
                    <Layers className="h-5 w-5 text-primary" />
                  </div>

                  <h3 className="mb-3 text-lg font-display font-semibold text-foreground sm:text-xl">
                    {project.title}
                  </h3>

                  <p className="mb-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {project.description}
                  </p>

                  <Link
                    to={project.link}
                    className="mt-auto inline-flex items-center justify-center gap-2 text-primary hover:underline sm:justify-start"
                  >
                    View Service
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Button variant="gradient" size="lg" asChild>
              <Link to="/work" className="inline-flex items-center gap-2">
                See Our Work
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <div className="rounded-[28px] border border-border/70 bg-card/40 px-5 py-10 shadow-soft backdrop-blur-sm sm:px-8 sm:py-12 lg:px-12 lg:py-14">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-10 text-center sm:mb-12"
            >
              <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
                What Richmond Businesses{" "}
                <span className="gradient-text">Usually Ask First</span>
              </h2>
            </motion.div>

            <div className="mx-auto max-w-[1000px] space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={faq.question}
                  className="relative z-10 overflow-hidden rounded-xl border border-border bg-background"
                >
                  <button
                    type="button"
                    onPointerUp={() =>
                      setOpenFaq(openFaq === index ? null : index)
                    }
                    className="pointer-events-auto flex w-full touch-manipulation items-center justify-between p-5 text-left transition-colors hover:bg-accent/50 sm:p-6"
                  >
                    <span className="pr-6 font-semibold text-foreground">
                      {faq.question}
                    </span>

                    <ChevronDown
                      className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-300 ${
                        openFaq === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                      openFaq === index
                        ? "grid-rows-[1fr]"
                        : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 pb-5 leading-relaxed text-muted-foreground sm:px-6 sm:pb-6">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: faqs.map((faq) => ({
                  "@type": "Question",
                  name: faq.question,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: faq.answer,
                  },
                })),
              })}
            </script>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-[28px] border border-border/70 bg-card/40 px-6 py-14 text-center shadow-soft backdrop-blur-sm sm:px-10 sm:py-16 lg:px-16 lg:py-18"
          >
            <div className="mx-auto max-w-[1000px]">
              <h2 className="mb-4 text-3xl font-display font-bold text-foreground sm:mb-6 md:text-4xl">
                Your Richmond Business Has a Digital Problem. We Build the
                Solution.
              </h2>

              <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                Let us talk about what your business actually needs online.
              </p>

              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full justify-center sm:w-auto"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}